import {
  Radio,
  ChartColumnIncreasing,
  Blocks,
  CalendarClock,
  ScanSearch,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

interface SectionDefinition {
  id: string;
  label: string;
  href: string;
  basePath: string;
  icon: LucideIcon;
  description: string;
}

export const PRODUCT_PROFILES = [
  "viewer",
  "client-viewer",
  "nagra-analyst",
  "nagra-ops",
  "nagra-officer",
  "nagra-admin",
] as const;
export type ProductProfile = (typeof PRODUCT_PROFILES)[number];
export const DEFAULT_PROFILE: ProductProfile = "nagra-ops";

/**
 * Profiles a person can be given or previewed as. `viewer` is a legacy,
 * read-only subset of `client-viewer`: it stays resolvable (an unknown profile
 * would fall back to `DEFAULT_PROFILE`, widening access) but is no longer offered.
 */
export const SELECTABLE_PROFILES = PRODUCT_PROFILES.filter(
  (profile): profile is Exclude<ProductProfile, "viewer"> => profile !== "viewer",
);

/** Client profiles get a reduced product: no analyst vocabulary, search, tenant scope or sharing controls. */
export function isClientProfile(profile: string | undefined): boolean {
  return profile === "client-viewer";
}

/** How a profile key reads in the UI: `nagra-analyst` → "NAGRA analyst", `client-viewer` → "Client viewer". */
export function profileLabel(profile: string): string {
  const [first, ...rest] = profile.split("-");
  const lead = first === "nagra" ? "NAGRA" : first.charAt(0).toUpperCase() + first.slice(1);
  return [lead, ...rest].join(" ");
}

/** The space holding the dashboards an account can open: shared with its organization, not personal. */
export const WORKSPACE_LABEL = "Workspace";

/**
 * How each space is named in the UI. Routes and ids are persisted contracts and
 * never change with a label: renaming a space is an edit here, nowhere else.
 */
export const SECTION_LABELS = {
  live: "Live Monitoring",
  operations: "Operations Planning",
  investigations: "Investigations",
  measurement: "Post-event Reporting",
  workspace: WORKSPACE_LABEL,
  admin: "Administration",
} as const;

export const SECTION_DEFINITIONS = [
  {
    id: "operations",
    label: SECTION_LABELS.operations,
    href: "/operations",
    basePath: "/operations",
    icon: CalendarClock,
    description: "Prepare campaigns, schedules and monitoring.",
  },
  {
    id: "live",
    label: SECTION_LABELS.live,
    href: "/live",
    basePath: "/live",
    icon: Radio,
    description: "Follow live events, protection and system health.",
  },
  {
    id: "investigations",
    label: SECTION_LABELS.investigations,
    href: "/investigations",
    basePath: "/investigations",
    icon: ScanSearch,
    description: "Identify, investigate and document suspected pirate operations.",
  },
  {
    id: "measurement",
    label: SECTION_LABELS.measurement,
    href: "/measurement",
    basePath: "/measurement",
    icon: ChartColumnIncreasing,
    description: "Review post-event results and the impact of protection.",
  },
  {
    id: "workspace",
    label: SECTION_LABELS.workspace,
    href: "/workspace",
    basePath: "/workspace",
    icon: Blocks,
    description: "Your dashboards and account activity.",
  },
  {
    id: "admin",
    label: SECTION_LABELS.admin,
    href: "/admin",
    basePath: "/admin",
    icon: ShieldCheck,
    description: "Manage accounts, profiles, scopes and tenants.",
  },
] as const satisfies readonly SectionDefinition[];

/** Journal and the dashboard catalogue remain separate permissions, not spaces. */
export type SectionId = (typeof SECTION_DEFINITIONS)[number]["id"] | "journal" | "dashboards";

const OPERATOR_SECTIONS: readonly SectionId[] = [
  "live",
  "journal",
  "measurement",
  "workspace",
  "dashboards",
];

export const PROFILE_SECTIONS: Record<ProductProfile, readonly SectionId[]> = {
  viewer: ["measurement", "workspace", "dashboards"],
  // Home and Workspace only. Dashboards open from the workspace itself.
  "client-viewer": ["workspace"],
  "nagra-analyst": OPERATOR_SECTIONS,
  "nagra-ops": [...OPERATOR_SECTIONS, "operations", "investigations"],
  "nagra-officer": [...OPERATOR_SECTIONS, "operations", "investigations"],
  "nagra-admin": [...SECTION_DEFINITIONS.map(({ id }) => id), "journal", "dashboards"],
};

export function getProfileSections(profile: string | undefined): readonly SectionId[] {
  return PROFILE_SECTIONS[profile as ProductProfile] ?? PROFILE_SECTIONS[DEFAULT_PROFILE];
}

export const SECTION_IDS: readonly SectionId[] = [
  ...SECTION_DEFINITIONS.map(({ id }) => id),
  "journal",
  "dashboards",
];

interface UiConfig {
  enabledSections: readonly SectionId[];
}

export const uiConfig: UiConfig = {
  enabledSections: [
    "workspace",
    "admin",
    "dashboards",
    "operations",
    "investigations",
    "live",
    "measurement",
  ],
};

export function getEnabledSections(demoEnabled: boolean): readonly SectionId[] {
  return demoEnabled ? SECTION_IDS : uiConfig.enabledSections;
}
